import { useHead } from '@unhead/vue'
import { getCurrentInstance, computed, unref, type ComputedRef, type MaybeRef } from 'vue'
import { SUPPORTED_LOCALES, type Locale } from '@/i18n'

interface PageMetaOptions {
  title?: MaybeRef<string>
  description?: MaybeRef<string>
  canonicalUrl?: MaybeRef<string>
  ogImage?: MaybeRef<string>
  ogImageAlt?: MaybeRef<string>
  ogType?: MaybeRef<string>
  robots?: MaybeRef<string>
  structuredData?: MaybeRef<Record<string, any> | Record<string, any>[]>
}

const BASE_URL = 'https://skinwhales.com'
const DEFAULT_OG_IMAGE = `${BASE_URL}/assets/og-image.webp`
const DEFAULT_DESCRIPTION =
  'Provably fair CS2 case opening with free daily cases, instant withdrawals, and transparent odds. Join Skinwhales today!'
const DEFAULT_TITLE = 'Skinwhales'

// Global structured data schemas
const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Skinwhales',
  url: BASE_URL,
  logo: `${BASE_URL}/assets/logo.png`,
  sameAs: [
    'https://x.com/skinwhaless',
    'https://instagram.com/skinwhales',
    'https://t.me/skinwhales',
    'https://discord.gg/veXFs2xstD',
  ],
}

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Skinwhales',
  url: BASE_URL,
}

export function usePageMeta(options: PageMetaOptions = {}) {
  const instance = getCurrentInstance()
  const i18n = instance?.appContext.config.globalProperties.$i18n
  const router = instance?.appContext.config.globalProperties.$router

  // Make locale and route reactive
  const locale = computed(() => i18n?.locale.value || 'en')
  const currentRoute = computed(() => router?.currentRoute.value)

  // Convert all options to computed refs for reactivity
  const fullTitle = computed(() => unref(options.title) || DEFAULT_TITLE)
  const description = computed(() => unref(options.description) || DEFAULT_DESCRIPTION)
  const ogImage = computed(() => unref(options.ogImage) || DEFAULT_OG_IMAGE)
  const ogImageAlt = computed(() => unref(options.ogImageAlt) || 'Skinwhales - CS2 Case Opening Platform')
  const ogType = computed(() => unref(options.ogType) || 'website')
  const robots = computed(() => unref(options.robots) || 'index, follow')

  // Determine canonical URL
  const canonicalUrl = computed(() => unref(options.canonicalUrl) || `${BASE_URL}${currentRoute.value?.path || ''}`)

  // Map locale to OG locale format
  const localeToOgLocale = (loc: string): string => {
    const map: Record<string, string> = {
      en: 'en_US',
      tr: 'tr_TR',
      es: 'es_ES',
      ru: 'ru_RU',
      pl: 'pl_PL',
      de: 'de_DE',
      ro: 'ro_RO',
    }
    return map[loc] || 'en_US'
  }

  const currentOgLocale = computed(() => localeToOgLocale(locale.value))
  const alternateOgLocales = computed(() =>
    SUPPORTED_LOCALES.filter((loc) => loc !== locale.value).map(localeToOgLocale)
  )

  // Build meta tags as computed
  const metaTags = computed(() => [
    // Basic meta
    { name: 'description', content: description.value },
    { name: 'robots', content: robots.value },

    // Open Graph
    { property: 'og:type', content: ogType.value },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:title', content: fullTitle.value },
    { property: 'og:description', content: description.value },
    { property: 'og:image', content: ogImage.value },
    { property: 'og:image:alt', content: ogImageAlt.value },
    { property: 'og:site_name', content: 'Skinwhales' },
    { property: 'og:locale', content: currentOgLocale.value },
    ...alternateOgLocales.value.map((loc) => ({ property: 'og:locale:alternate', content: loc })),

    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: canonicalUrl.value },
    { name: 'twitter:title', content: fullTitle.value },
    { name: 'twitter:description', content: description.value },
    { name: 'twitter:image', content: ogImage.value },
    { name: 'twitter:image:alt', content: ogImageAlt.value },
  ])

  // Build hreflang links as computed
  const hreflangLinks = computed(() => {
    const currentPath = currentRoute.value?.path || ''
    const links: Array<{ rel: string; hreflang: string; href: string }> =
      SUPPORTED_LOCALES.map((lang) => ({
        rel: 'alternate',
        hreflang: lang,
        href: lang === 'en' ? `${BASE_URL}${currentPath}` : `${BASE_URL}/${lang}${currentPath}`,
      }))

    // Add x-default hreflang (pointing to English)
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${BASE_URL}${currentPath}`,
    })

    return links
  })

  // Add structured data with computed
  const schemas = computed(() => {
    const base: Record<string, any>[] = [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]

    const structuredData = unref(options.structuredData)
    if (structuredData) {
      const pageSchemas = Array.isArray(structuredData) ? structuredData : [structuredData]
      base.push(...pageSchemas)
    }

    return base
  })

  // Build head config with computed values
  useHead({
    title: fullTitle,
    meta: metaTags,
    link: computed(() => [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
    ]),
    script: computed(() => schemas.value.map((schema) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    }))),
  })
}

// Helper to get full canonical URL
export function getCanonicalUrl(path: string): string {
  return `${BASE_URL}${path}`
}

// Helper to get full image URL
export function getFullImageUrl(imagePath: string): string {
  if (imagePath.startsWith('http')) return imagePath
  return `${BASE_URL}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`
}
